import React, { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ticketsApi } from '@/api/tickets.api';
import { Modal } from '@/components/ui/Modal';
import { Field } from '@/components/ui/Field';
import { ApiError } from '@/lib/errors';
import { useToasts } from '@/context/ToastContext';
import { invalidateAfterTicketAction } from '@/lib/queryInvalidation';
import { useTagTeammates } from './useTagTeammates';

interface Props {
  ticketId: string;
  open: boolean;
  onClose: () => void;
}

export function CloseTicketModal({ ticketId, open, onClose }: Props) {
  const [outcome, setOutcome] = useState('resolved');
  const [resolutionNotes, setResolutionNotes] = useState('');
  const [deflected, setDeflected] = useState(false);
  const [cxInformedNow, setCxInformedNow] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const { push } = useToasts();
  const tag = useTagTeammates();

  const mutation = useMutation({
    mutationFn: async () => {
      const res = await ticketsApi.close(ticketId, {
        outcome,
        resolution_notes: resolutionNotes || undefined,
        deflected,
        cx_informed_now: cxInformedNow
      });
      if (tag.hasContent) await ticketsApi.tag(ticketId, tag.payload());
      return res;
    },
    onSuccess: (result) => {
      push({
        kind: result.warning ? 'warning' : 'success',
        title: 'Ticket closed',
        message: result.warning ?? undefined
      });
      invalidateAfterTicketAction(queryClient, ticketId);
      tag.reset();
      onClose();
    },
    onError: (err: unknown) => {
      setError(err instanceof ApiError ? err.problem?.detail ?? err.message : 'Failed to close ticket');
    }
  });

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Close ticket"
      footer={
        <>
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={mutation.isPending}
            onClick={() => mutation.mutate()}
          >
            {mutation.isPending ? 'Closing...' : 'Close ticket'}
          </button>
        </>
      }
    >
      <Field label="Outcome" required>
        <input
          className="form-input"
          value={outcome}
          onChange={(e) => setOutcome(e.target.value)}
          maxLength={100}
        />
      </Field>
      <Field label="Resolution notes (optional)">
        <textarea
          className="form-textarea"
          rows={3}
          value={resolutionNotes}
          onChange={(e) => setResolutionNotes(e.target.value)}
        />
      </Field>
      <div className="flex gap-4 wrap">
        <label className="flex items-center gap-2">
          <input type="checkbox" checked={deflected} onChange={(e) => setDeflected(e.target.checked)} />
          <span className="text-small">Deflected (no refund/replacement issued)</span>
        </label>
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={cxInformedNow}
            onChange={(e) => setCxInformedNow(e.target.checked)}
          />
          <span className="text-small">Customer informed just now</span>
        </label>
      </div>
      {tag.render()}
      {error ? <div className="form-error mt-2">{error}</div> : null}
    </Modal>
  );
}
